import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ClipboardList,
  RefreshCw,
  AlertTriangle,
  Lock,
  Pencil,
  UserCheck,
  Check,
  X,
  Paperclip,
  Send,
  CircleDot,
  Eye,
  EyeOff,
  CheckCircle2,
  ShieldAlert,
  History,
  MessageCircle,
  Info,
  FileText,
  ListChecks,
  CalendarClock,
  Layers,
  Hourglass,
  RotateCcw,
  Download,
} from "lucide-react";
import { useTheme } from "../../context/themeContext";
import { useAuth } from "../../context/authContext";
import {
  BRAND,
  TOKENS,
  CardShell,
  STATUS_META,
  PRIORITY_META,
  TRACK_STEPS,
  fmtDate,
  fmtDateTime,
  fmtRelative,
  fmtDayLabel,
  dayKey,
  apiRequest,
  StatusBadge,
  PriorityBadge,
  CategoryTag,
  ShareBadge,
  Toast,
  useConfirm,
  AttachmentRow,
} from "./GrievanceShared";

// ---------------------------------------------------------------------------
// SectionHeader — consistent icon + title (+ optional right-aligned action
// and count badge) used at the top of every card on this page.
// ---------------------------------------------------------------------------

function SectionHeader({ icon: Icon, title, tint, action, count, t }) {
  return (
    <div
      className="mb-3.5 flex items-center justify-between gap-2 border-b pb-3"
      style={{ borderColor: t.gridStroke }}
    >
      <div className="flex items-center gap-2">
        <span
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
          style={{
            backgroundColor: `${tint || BRAND.purple}1f`,
            color: tint || BRAND.purple,
          }}
        >
          <Icon size={14} strokeWidth={2.5} />
        </span>
        <h4 className={"text-sm font-bold " + t.textPrimary}>{title}</h4>
        {typeof count === "number" && (
          <span
            className={
              "rounded-full px-1.5 py-0.5 text-[10px] font-bold " +
              t.surface +
              " " +
              t.textFaint
            }
          >
            {count}
          </span>
        )}
      </div>
      {action}
    </div>
  );
}
// ---------------------------------------------------------------------------
// NEW — Attachment preview modal. Clicking an attachment row anywhere
// except its download control opens the image in a popup on the same
// page. Clicking anywhere outside the popup (the overlay) closes it.
// ---------------------------------------------------------------------------

function isImageAttachment(a) {
  if (!a) return false;
  if (a.mimeType) return a.mimeType.startsWith("image/");
  const name = a.originalName || a.filename || a.url || "";
  return /\.(png|jpe?g|gif|webp|svg|bmp)(\?.*)?$/i.test(name);
}

async function forceDownload(url, filename) {
  try {
    const response = await fetch(url, { mode: "cors" });
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    // If fetch/blob fails (e.g. CORS-restricted asset), fall back to
    // opening it directly rather than doing nothing.
    window.open(url, "_blank", "noreferrer");
  }
}

// Used on rows built from <AttachmentRow/> (whose internals we don't
// control) — if the click landed on a link/button (the row's own
// download control), let that behave normally. Otherwise treat it as
// "open preview".
function handleAttachmentRowClick(e, attachment, onPreview) {
  const hitControl = e.target.closest("a,button");
  if (hitControl) {
    // This is AttachmentRow's own download icon/button — force an actual
    // file save instead of whatever it does by default (e.g. opening a
    // new tab), then stop here.
    e.preventDefault();
    e.stopPropagation();
    forceDownload(
      attachment.url,
      attachment.originalName || attachment.filename,
    );
    return;
  }
  if (!isImageAttachment(attachment)) return; // non-images: no preview
  e.preventDefault();
  e.stopPropagation();
  onPreview(attachment);
}

function AttachmentPreviewModal({ attachment, onClose, t, isDark }) {
  useEffect(() => {
    if (!attachment) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [attachment, onClose]);

  if (!attachment) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className={
          "relative flex max-h-[90vh] max-w-[90vw] flex-col overflow-hidden rounded-2xl shadow-2xl " +
          (isDark ? "bg-[#0c0d1f]" : "bg-white")
        }
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white transition-colors hover:bg-black/75"
          aria-label="Close preview"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
        <div className="flex min-h-0 flex-1 items-center justify-center bg-black/5">
          <img
            src={attachment.url}
            alt={attachment.originalName || "Attachment preview"}
            className="max-h-[80vh] max-w-[90vw] object-contain"
          />
        </div>
        <div
          className="flex items-center justify-between gap-3 border-t px-4 py-2.5"
          style={{ borderColor: t.gridStroke }}
        >
          <span className={"truncate text-xs font-medium " + t.textMuted}>
            {attachment.originalName}
          </span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status tracker — shipment-style progress strip.
// ---------------------------------------------------------------------------

function StatusTracker({ status, statusHistory = [] }) {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const isEscalated = status === "escalated";
  const effectiveStatus = [
    "escalated",
    "awaiting_response",
    "on_hold",
    "closed",
  ].includes(status)
    ? status === "closed"
      ? "resolved"
      : "in_progress"
    : status;
  const currentIndex = Math.max(
    0,
    TRACK_STEPS.findIndex((s) => s.key === effectiveStatus),
  );

  const stepDate = (key) => {
    const hit = statusHistory.find((h) => h.status === key);
    return hit ? fmtDate(hit.timestamp) : null;
  };

  return (
    <div>
      {isEscalated && (
        <div
          className="mb-3 flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold"
          style={{ backgroundColor: `${BRAND.red}1a`, color: BRAND.red }}
        >
          <ShieldAlert size={14} />
          This case has been escalated and needs priority attention.
        </div>
      )}
      <div className="flex items-start overflow-x-auto pb-1">
        {TRACK_STEPS.map((step, i) => {
          const Icon = step.icon;
          const isDone =
            i < currentIndex || status === "resolved" || status === "closed";
          const isCurrent =
            i === currentIndex && status !== "resolved" && status !== "closed";
          const active = isDone || isCurrent;
          const iconTint =
            isEscalated && isCurrent
              ? BRAND.red
              : active
                ? BRAND.teal
                : undefined;
          return (
            <React.Fragment key={step.key}>
              <div className="flex w-[100px] shrink-0 flex-col items-center gap-1.5 text-center">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-full border-2"
                  style={{
                    backgroundColor: active ? `${iconTint}1f` : "transparent",
                    borderColor: active ? iconTint : t.gridStroke,
                    color: active ? iconTint : undefined,
                  }}
                >
                  <Icon
                    size={16}
                    strokeWidth={2.5}
                    className={!active ? t.textFaint : ""}
                  />
                </span>
                <span
                  className={
                    "text-[11px] font-semibold " +
                    (active ? t.textPrimary : t.textFaint)
                  }
                >
                  {isEscalated && isCurrent ? "Escalated" : step.label}
                </span>
                <span className={"text-[10px] " + t.textFaint}>
                  {stepDate(step.key) || "—"}
                </span>
              </div>
              {i < TRACK_STEPS.length - 1 && (
                <div
                  className="mt-[19px] h-0.5 flex-1 min-w-[24px]"
                  style={{
                    backgroundColor:
                      i < currentIndex ||
                      status === "resolved" ||
                      status === "closed"
                        ? BRAND.teal
                        : t.gridStroke,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assign panel
// ---------------------------------------------------------------------------

function AssignPanel({ assignedTo, handlers, onAssign, t }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(assignedTo || "Unassigned");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(assignedTo || "Unassigned");
  }, [assignedTo]);

  const save = async () => {
    setSaving(true);
    try {
      await onAssign(value.trim() || "Unassigned");
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <div className={"rounded-2xl px-3 py-2.5 " + t.surfaceSoft}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div
              className={"text-[10px] font-semibold uppercase " + t.textFaint}
            >
              Handler
            </div>
            <div
              className={
                "mt-0.5 text-sm font-bold " +
                (assignedTo === "Unassigned" || !assignedTo
                  ? t.textFaint + " italic"
                  : t.textPrimary)
              }
            >
              {assignedTo || "Unassigned"}
            </div>
          </div>
          <button
            onClick={() => setEditing(true)}
            className={
              "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors " +
              t.surface +
              " " +
              t.surfaceHover +
              " " +
              t.textMuted
            }
            aria-label="Assign handler"
          >
            <Pencil size={12} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={"rounded-2xl px-3 py-2.5 " + t.surfaceSoft}>
      <div
        className={
          "flex items-center gap-1.5 text-[10px] font-semibold uppercase " +
          t.textFaint
        }
      >
        <UserCheck size={11} /> Assign Handler
      </div>
      <div className="mt-1.5 flex items-center gap-1.5">
        <input
          list="grievance-handler-options"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Type or pick a name…"
          className={
            "min-w-0 flex-1 rounded-lg px-2.5 py-1.5 text-xs outline-none " +
            t.inputBg +
            " " +
            t.textPrimary
          }
        />
        <datalist id="grievance-handler-options">
          {handlers.map((h) => (
            <option key={h} value={h} />
          ))}
        </datalist>
        <button
          onClick={save}
          disabled={saving}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white disabled:opacity-50"
          style={{ background: BRAND.gradient }}
          aria-label="Save handler"
        >
          <Check size={13} strokeWidth={2.5} />
        </button>
        <button
          onClick={() => {
            setEditing(false);
            setValue(assignedTo || "Unassigned");
          }}
          className={
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors " +
            t.surface +
            " " +
            t.surfaceHover +
            " " +
            t.textMuted
          }
          aria-label="Cancel"
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Update panel — status + priority editor.
// NEW: accepts a `disabled` prop. When the case is resolved/closed, this
// renders as a disabled, non-interactive button (the case must be reopened
// via ReopenCaseButton before status/priority can be edited again).
// ---------------------------------------------------------------------------

function UpdateCasePanel({ status, priority, onUpdate, disabled, t }) {
  const [editing, setEditing] = useState(false);
  const [nextStatus, setNextStatus] = useState(status);
  const [nextPriority, setNextPriority] = useState(priority);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setNextStatus(status);
    setNextPriority(priority);
  }, [status, priority]);

  useEffect(() => {
    // If the case becomes locked while the editor is open, close it.
    if (disabled) setEditing(false);
  }, [disabled]);

  const dirty = nextStatus !== status || nextPriority !== priority;

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate({ status: nextStatus, priority: nextPriority });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        onClick={() => !disabled && setEditing(true)}
        disabled={disabled}
        title={
          disabled
            ? "Reopen the case to update its status or priority"
            : undefined
        }
        className={
          "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed " +
          t.pillBorder +
          " " +
          t.surfaceSoft +
          " " +
          (disabled ? "" : t.surfaceHover + " ") +
          t.textPrimary
        }
      >
        <Pencil size={12} strokeWidth={2.5} />
        Update Status / Priority
      </button>
    );
  }

  return (
    <div className={"rounded-2xl p-3 " + t.surfaceSoft}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex-1 min-w-[140px]">
          <label
            className={
              "mb-1 block text-[10px] font-semibold uppercase " + t.textFaint
            }
          >
            Status
          </label>
          <select
            value={nextStatus}
            onChange={(e) => setNextStatus(e.target.value)}
            className={
              "w-full rounded-lg px-2.5 py-1.5 text-xs outline-none " +
              t.inputBg +
              " " +
              t.textPrimary
            }
          >
            {Object.entries(STATUS_META).map(([key, meta]) => (
              <option key={key} value={key}>
                {meta.label}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label
            className={
              "mb-1 block text-[10px] font-semibold uppercase " + t.textFaint
            }
          >
            Priority
          </label>
          <select
            value={nextPriority}
            onChange={(e) => setNextPriority(e.target.value)}
            className={
              "w-full rounded-lg px-2.5 py-1.5 text-xs outline-none " +
              t.inputBg +
              " " +
              t.textPrimary
            }
          >
            {Object.entries(PRIORITY_META).map(([key, meta]) => (
              <option key={key} value={key}>
                {meta.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="mt-2.5 flex gap-2">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
          style={{ background: BRAND.gradient }}
        >
          <Check size={13} /> Save Changes
        </button>
        <button
          onClick={() => setEditing(false)}
          className={
            "rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors " +
            t.pillBorder +
            " " +
            t.surface +
            " " +
            t.surfaceHover +
            " " +
            t.textMuted
          }
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NEW: Reopen button — shown only when the case is resolved/closed. Fires
// the passed-in onReopen handler, which is expected to show its own
// confirmation dialog before actually calling the API.
// ---------------------------------------------------------------------------

function ReopenCaseButton({ onReopen, loading }) {
  return (
    <button
      onClick={onReopen}
      disabled={loading}
      className="flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11px] font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
      style={{ background: BRAND.gradient }}
    >
      <RotateCcw size={12} strokeWidth={2.5} />
      {loading ? "Reopening…" : "Reopen Case"}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Attachments panel
// ---------------------------------------------------------------------------

function AttachmentsPanel({ attachments, onUpload, onDelete, onPreview, t }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    try {
      await onUpload(Array.from(fileList));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div>
      <SectionHeader
        icon={Paperclip}
        title="Attachments"
        tint={BRAND.teal}
        count={attachments.length}
        t={t}
        action={
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={
              "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors disabled:opacity-50 " +
              t.pillBorder +
              " " +
              t.surfaceSoft +
              " " +
              t.surfaceHover +
              " " +
              t.textPrimary
            }
          >
            <Paperclip size={12} strokeWidth={2.5} />
            {uploading ? "Uploading…" : "Upload"}
          </button>
        }
      />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {attachments.length === 0 ? (
        <p className={"text-xs " + t.textFaint}>
          No attachments yet. PDF, DOC/DOCX, and images are supported.
        </p>
      ) : (
        <div className="max-h-64 space-y-1.5 overflow-y-auto pr-1">
          {attachments.map((a) => (
            <div
              key={a._id}
              onClick={(e) => handleAttachmentRowClick(e, a, onPreview)}
              className={isImageAttachment(a) ? "cursor-pointer" : undefined}
            >
              <AttachmentRow
                attachment={a}
                t={t}
                onDelete={() => onDelete(a)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Timeline entry
// ---------------------------------------------------------------------------

function TimelineEntry({
  event,
  isLast,
  isOriginal,
  onToggleShare,
  onPreview,
  t,
  isDark,
}) {
  const [toggling, setToggling] = useState(false);

  const isFromEmployee = event.actor?.toLowerCase() === "employee";
  // The very first timeline entry is always the original grievance
  // submission (the complaint message + any attachments the employee
  // filed with it). HR must never be able to unshare that from the
  // employee — it's the employee's own submission, not an HR update —
  // so we hide the share/unshare control for it regardless of how the
  // backend tagged the "actor" on that entry.
  const canToggleShare = !isFromEmployee && !isOriginal;

  const toggle = async () => {
    setToggling(true);
    try {
      await onToggleShare(event);
    } finally {
      setToggling(false);
    }
  };

  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: `${BRAND.purple}22`, color: BRAND.purple }}
        >
          <CircleDot size={12} strokeWidth={2.5} />
        </span>
        {!isLast && (
          <span
            className={
              "w-px flex-1 " + (isDark ? "bg-white/10" : "bg-black/10")
            }
          />
        )}
      </div>
      <div className="flex-1 pb-4">
        <div
          className={
            "flex flex-wrap items-center gap-2 text-sm font-semibold " +
            t.textPrimary
          }
        >
          {event.action}
          {canToggleShare && <ShareBadge shared={!!event.visibleToEmployee} />}
        </div>
        {event.note && (
          <p className={"mt-0.5 text-sm " + t.textMuted}>{event.note}</p>
        )}

        {!!event.attachments?.length && (
          <div className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {event.attachments.map((a) => (
              <div
                key={a._id || a.filename}
                onClick={(e) => handleAttachmentRowClick(e, a, onPreview)}
                className={isImageAttachment(a) ? "cursor-pointer" : undefined}
              >
                <AttachmentRow attachment={a} t={t} />
              </div>
            ))}
          </div>
        )}

        <div
          className={
            "mt-1.5 flex flex-wrap items-center gap-2 text-[11px] " +
            t.textFaint
          }
        >
          <span>
            {event.actor} · {fmtDateTime(event.timestamp)}
          </span>
          {canToggleShare && (
            <button
              onClick={toggle}
              disabled={toggling}
              className={
                "flex items-center gap-1 rounded-full border px-2 py-0.5 font-semibold transition-colors disabled:opacity-50 " +
                t.pillBorder +
                " " +
                t.surfaceSoft +
                " " +
                t.surfaceHover +
                " " +
                t.textMuted
              }
            >
              {event.visibleToEmployee ? (
                <EyeOff size={10} />
              ) : (
                <Eye size={10} />
              )}
              {event.visibleToEmployee ? "Unshare" : "Share with Employee"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Follow-up conversation — redesigned as a proper chat panel: sticky
// composer, date separators, avatars, auto-scroll to latest message.
// NEW: accepts a `disabled` prop. When the case is resolved/closed, the
// composer is replaced with a locked notice and no message can be sent
// until the case is reopened.
// ---------------------------------------------------------------------------

function ConversationPanel({
  conversation,
  loading,
  onSend,
  disabled,
  onPreview,

  t,
  isDark,
}) {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [conversation, loading]);

  const removeFile = (index) => {
    setFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0 && fileInputRef.current)
        fileInputRef.current.value = "";
      return next;
    });
  };

  const send = async () => {
    if (disabled) return;
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      await onSend(message.trim(), files);
      setMessage("");
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // group into day sections for date separators
  let lastDay = null;

  return (
    <div>
      <SectionHeader
        icon={MessageCircle}
        title="Follow-up Conversation"
        tint={BRAND.purple}
        count={conversation.length}
        t={t}
      />

      <div
        ref={scrollRef}
        className={
          "mx-auto max-h-[360px] max-w-3xl space-y-3 overflow-y-auto rounded-2xl p-4 " +
          t.surfaceSoft
        }
      >
        {loading ? (
          <div
            className={
              "flex h-full items-center justify-center gap-2 text-xs " +
              t.textMuted
            }
          >
            <RefreshCw size={13} className="animate-spin" /> Loading
            conversation…
          </div>
        ) : conversation.length === 0 ? (
          <div
            className={
              "flex h-full flex-col items-center justify-center gap-2 text-center " +
              t.textFaint
            }
          >
            <MessageCircle size={26} />
            <p className="text-xs">
              No follow-up messages yet. Send the first one below.
            </p>
          </div>
        ) : (
          conversation.map((m) => {
            const fromHR = m.sender === "hr";
            const key = dayKey(m.timestamp);
            const showSeparator = key !== lastDay;
            lastDay = key;
            return (
              <React.Fragment key={m._id}>
                {showSeparator && (
                  <div className="flex items-center justify-center py-1">
                    <span
                      className={
                        "rounded-full px-3 py-1 text-[10px] font-semibold " +
                        t.surface +
                        " " +
                        t.textFaint
                      }
                    >
                      {fmtDayLabel(m.timestamp)}
                    </span>
                  </div>
                )}
                <div
                  className={
                    "flex items-end gap-2 " +
                    (fromHR ? "justify-end" : "justify-start")
                  }
                >
                  {!fromHR && (
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                      style={{
                        backgroundColor: `${BRAND.silver}25`,
                        color: BRAND.silver,
                      }}
                    >
                      EM
                    </span>
                  )}
                  <div
                    className={
                      "max-w-[75%] rounded-2xl px-3.5 py-2.5 text-sm shadow-sm " +
                      (fromHR
                        ? "text-white rounded-br-md"
                        : "rounded-bl-md " + t.surface + " " + t.textPrimary)
                    }
                    style={fromHR ? { background: BRAND.gradient } : undefined}
                  >
                    {m.message && (
                      <p className="leading-relaxed">{m.message}</p>
                    )}
                    {!!m.attachments?.length && (
                      <div className="mt-1.5 space-y-1">
                        {m.attachments.map((a) => {
                          const isImg = isImageAttachment(a);
                          return (
                            <div
                              key={a._id || a.filename}
                              onClick={() => {
                                if (isImg) onPreview(a);
                                else window.open(a.url, "_blank", "noreferrer");
                              }}
                              className={
                                "flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-medium " +
                                (fromHR ? "bg-white/15" : t.surfaceSoft)
                              }
                            >
                              <Paperclip size={10} className="shrink-0" />
                              <span className="min-w-0 flex-1 truncate">
                                {a.originalName}
                              </span>

                              <a
                                target="_blank"
                                href={a.url}
                                download={a.originalName}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  forceDownload(a.url, a.originalName);
                                }}
                                className="shrink-0 opacity-80 hover:opacity-100"
                                aria-label={`Download ${a.originalName}`}
                              >
                                <Download size={10} />
                              </a>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div className=" text-[10px] opacity-70">
                      {fmtRelative(m.timestamp)}
                    </div>
                  </div>
                  {fromHR && (
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white"
                      style={{ background: BRAND.gradient }}
                    >
                      HR
                    </span>
                  )}
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>

      {disabled ? (
        <div
          className={
            "mx-auto mt-2.5 flex max-w-3xl items-center justify-center gap-2 rounded-2xl border px-4 py-3 text-center text-xs font-medium " +
            t.pillBorder +
            " " +
            t.surfaceSoft +
            " " +
            t.textFaint
          }
        >
          <Lock size={12} />
          This case is closed for messaging. Reopen the case to send a new
          follow-up message.
        </div>
      ) : (
        <div
          className={
            "mx-auto mt-2.5 max-w-3xl rounded-2xl border p-2.5 " +
            t.pillBorder +
            " " +
            t.surfaceSoft
          }
        >
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Send a follow-up question to the employee… (Enter to send, Shift+Enter for new line)"
            rows={2}
            className={
              "w-full resize-none bg-transparent px-2 py-1.5 text-sm outline-none placeholder:" +
              t.textFaint +
              " " +
              t.textPrimary
            }
          />
          {files.length > 0 && (
            <div className="flex flex-wrap gap-1.5 px-2 pb-1.5">
              {files.map((f, i) => (
                <span
                  key={i}
                  className={
                    "flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-[10px] " +
                    t.surface +
                    " " +
                    t.textMuted
                  }
                >
                  <Paperclip size={9} /> {f.name}
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-black/10"
                    aria-label={`Remove ${f.name}`}
                  >
                    <X size={9} strokeWidth={3} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between px-1">
            <button
              onClick={() => fileInputRef.current?.click()}
              className={
                "flex items-center gap-1.5 rounded-full px-2.5 py-1.5 text-[11px] font-semibold transition-colors " +
                t.surfaceHover +
                " " +
                t.textMuted
              }
            >
              <Paperclip size={12} /> Attach
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,image/*"
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
            <button
              onClick={send}
              disabled={sending || (!message.trim() && files.length === 0)}
              className="flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
              style={{ background: BRAND.gradient }}
            >
              <Send size={13} />
              {sending ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Activity history — redesigned as a compact audit trail with per-action
// icon coloring and relative timestamps, grouped by day.
// ---------------------------------------------------------------------------

const ACTIVITY_ICON_TINTS = [
  BRAND.purple,
  BRAND.teal,
  BRAND.pink,
  BRAND.gold,
  BRAND.blue,
];

function activityTint(action = "") {
  let h = 0;
  for (let i = 0; i < action.length; i++)
    h = (h << 5) - h + action.charCodeAt(i);
  return ACTIVITY_ICON_TINTS[Math.abs(h) % ACTIVITY_ICON_TINTS.length];
}

function ActivityHistoryPanel({ activity, loading, t }) {
  let lastDay = null;
  return (
    <div>
      <SectionHeader
        icon={History}
        title="Activity History"
        tint={BRAND.purple}
        count={activity.length}
        t={t}
      />
      <div
        className={
          "max-h-72 space-y-2 overflow-y-auto rounded-2xl p-3 " + t.surfaceSoft
        }
      >
        {loading ? (
          <div
            className={
              "flex items-center justify-center gap-2 py-8 text-xs " +
              t.textMuted
            }
          >
            <RefreshCw size={13} className="animate-spin" /> Loading activity…
          </div>
        ) : activity.length === 0 ? (
          <div
            className={
              "flex flex-col items-center gap-2 py-8 text-center " + t.textFaint
            }
          >
            <Info size={20} />
            <p className="text-xs">No activity recorded yet.</p>
          </div>
        ) : (
          activity.map((a) => {
            const key = dayKey(a.timestamp);
            const showSeparator = key !== lastDay;
            lastDay = key;
            const tint = activityTint(a.action || "");
            return (
              <React.Fragment key={a._id}>
                {showSeparator && (
                  <div
                    className={
                      "px-1 pt-1 text-[10px] font-semibold uppercase tracking-wide " +
                      t.textFaint
                    }
                  >
                    {fmtDayLabel(a.timestamp)}
                  </div>
                )}
                <div
                  className={
                    "flex items-start gap-2.5 rounded-xl px-2.5 py-2.5 " +
                    t.surface
                  }
                >
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                    style={{ backgroundColor: `${tint}22`, color: tint }}
                  >
                    <CircleDot size={11} strokeWidth={2.5} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                      <span
                        className={"text-xs font-semibold " + t.textPrimary}
                      >
                        {a.action}
                      </span>
                      <span className={"shrink-0 text-[10px] " + t.textFaint}>
                        {fmtRelative(a.timestamp)}
                      </span>
                    </div>
                    {a.details && (
                      <p className={"mt-0.5 text-xs " + t.textMuted}>
                        {a.details}
                      </p>
                    )}
                    <div className={"mt-0.5 text-[10px] " + t.textFaint}>
                      {a.actor}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page — GrievanceCaseDetail. Route it as e.g.
// <Route path="/hr/grievances/:caseId" element={<GrievanceCaseDetail />} />
// ---------------------------------------------------------------------------

export default function GrievanceCaseDetail() {
  const { isDark } = useTheme();
  const t = TOKENS[isDark ? "dark" : "light"];
  const { code, token } = useAuth();
  const { caseId } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [handlers, setHandlers] = useState([]);

  const [conversation, setConversation] = useState([]);
  const [conversationLoading, setConversationLoading] = useState(true);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  const [note, setNote] = useState("");
  const [noteFiles, setNoteFiles] = useState([]);
  const noteFileInputRef = useRef(null);
  const [shareWithEmployee, setShareWithEmployee] = useState(false);
  const [submittingNote, setSubmittingNote] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [toast, setToast] = useState(null);

  const { ask: confirmAsk, dialog: confirmDialog } = useConfirm();

  const [previewAttachment, setPreviewAttachment] = useState(null); // NEW

  // A case is "locked" once it's resolved or closed — status/priority
  // editing and the follow-up chat composer are disabled until it's
  // explicitly reopened.
  const isCaseLocked = data?.status === "resolved" || data?.status === "closed";

  const showToast = useCallback(
    (message, type = "success") => setToast({ message, type }),
    [],
  );
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(timer);
  }, [toast]);

  // -------------------------------------------------------------------
  // API — GET a single case's full detail.
  // GET {serverUrl}api/v1/hr/grievances/:id
  // -------------------------------------------------------------------
  const loadCase = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      setError(false);
      try {
        const result = await apiRequest(`api/v1/hr/grievances/${caseId}`, {
          token,
        });
        setData(result);
      } catch (err) {
        console.error(err);
        setError(true);
        if (silent) showToast(err.message, "error");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [caseId, token, showToast],
  );

  useEffect(() => {
    loadCase();
    loadConversation();
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  // -------------------------------------------------------------------
  // API — GET distinct handlers already in use.
  // GET {serverUrl}api/v1/hr/handlers?code=
  // -------------------------------------------------------------------
  useEffect(() => {
    const loadHandlers = async () => {
      try {
        const params = new URLSearchParams();
        if (code) params.set("code", code);
        const result = await apiRequest(
          `api/v1/hr/handlers?${params.toString()}`,
          { token },
        );
        setHandlers(result);
      } catch (err) {
        console.error(err);
      }
    };
    loadHandlers();
  }, [code, token]);

  const loadConversation = async (silent = false) => {
    if (!silent) setConversationLoading(true);
    try {
      const result = await apiRequest(
        `api/v1/hr/grievances/${caseId}/conversation`,
        { token },
      );
      setConversation(result);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setConversationLoading(false);
    }
  };
  // Follow-up conversation messages/attachments belong ONLY in the
  // follow-up chat panel — never in the Activity History audit trail or
  // the Investigation Timeline. As a safety net (in case the backend
  // ever logs a conversation message as a generic activity row), we
  // filter anything that looks like a follow-up message out of the
  // activity feed here too.
  const isFollowUpActivity = (a) =>
    /follow[\s-]?up|conversation message/i.test(a?.action || "");

  const loadActivity = async () => {
    setActivityLoading(true);
    try {
      const result = await apiRequest(
        `api/v1/hr/grievances/${caseId}/activity`,
        { token },
      );
      setActivity((result || []).filter((a) => !isFollowUpActivity(a)));
    } catch (err) {
      console.error(err);
    } finally {
      setActivityLoading(false);
    }
  };

  // -------------------------------------------------------------------
  // API — POST a new investigation note (with optional attachments).
  // POST {serverUrl}api/v1/hr/grievances/:id/notes  (multipart)
  //
  // FIX: FormData values are always strings, so sending
  // "visibleToEmployee" -> "false" can still be read as truthy by a naive
  // Boolean() cast on the server. We now only include the field at all
  // when it's true, and omit it entirely otherwise, so an unchecked box
  // reliably stays private.
  // -------------------------------------------------------------------
  const addNote = async () => {
    if (!note.trim()) return;
    setSubmittingNote(true);
    try {
      const form = new FormData();
      form.set("note", note.trim());
      if (shareWithEmployee) {
        form.set("visibleToEmployee", "true");
      }
      noteFiles.forEach((f) => form.append("files", f));
      await apiRequest(`api/v1/hr/grievances/${caseId}/notes`, {
        method: "POST",
        body: form,
        token,
      });
      setNote("");
      setShareWithEmployee(false);
      setNoteFiles([]);
      if (noteFileInputRef.current) noteFileInputRef.current.value = "";
      await loadCase(true);
      loadActivity();
      showToast("Note added to the case.");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmittingNote(false);
    }
  };

  // -------------------------------------------------------------------
  // API — PATCH the assigned handler on a case.
  // PATCH {serverUrl}api/v1/hr/grievances/:id/assign  { assignedTo }
  // -------------------------------------------------------------------
  const assignHandler = async (name) => {
    try {
      await apiRequest(`api/v1/hr/grievances/${caseId}/assign`, {
        method: "PATCH",
        body: { assignedTo: name },
        token,
      });
      await loadCase(true);
      loadActivity();
      setHandlers((prev) =>
        name === "Unassigned" || prev.includes(name)
          ? prev
          : [...prev, name].sort(),
      );
      showToast(
        name === "Unassigned" ? "Handler unassigned." : `Assigned to ${name}.`,
      );
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // -------------------------------------------------------------------
  // API — PATCH a case's status and/or priority.
  // PATCH {serverUrl}api/v1/hr/grievances/:id/status  { status?, priority? }
  // -------------------------------------------------------------------
  const updateCase = async ({ status, priority }) => {
    try {
      await apiRequest(`api/v1/hr/grievances/${caseId}/status`, {
        method: "PATCH",
        body: { status, priority },
        token,
      });
      await loadCase(true);
      loadActivity();
      showToast("Case updated.");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const markResolved = () =>
    confirmAsk({
      title: "Resolve Complaint",
      message: `Mark ${caseId} as resolved? The employee's tracker will reflect this immediately.`,
      confirmLabel: "Mark Resolved",
      onConfirm: async () => {
        setResolving(true);
        try {
          await updateCase({ status: "resolved" });
        } finally {
          setResolving(false);
        }
      },
    });

  // -------------------------------------------------------------------
  // NEW — API: PATCH reopen a resolved/closed case.
  // PATCH {serverUrl}api/v1/hr/grievances/:id/reopen
  //
  // Expected backend behavior (see controller snippet provided
  // separately): move the case out of "resolved"/"closed" back into an
  // active status (e.g. "in_progress"), push a "Case reopened" entry
  // into both statusHistory and the activity log, and re-enable normal
  // note/status/conversation endpoints for this case going forward.
  // -------------------------------------------------------------------
  const reopenCase = () =>
    confirmAsk({
      title: "Reopen Case",
      message: `Reopen ${caseId}? This restores normal case handling — status/priority updates, investigation notes, and follow-up messaging will be enabled again.`,
      confirmLabel: "Reopen Case",
      onConfirm: async () => {
        setReopening(true);
        try {
          await apiRequest(`api/v1/hr/grievances/${caseId}/reopen`, {
            method: "PATCH",
            token,
          });
          await loadCase(true);
          loadActivity();
          showToast("Case reopened.");
        } catch (err) {
          showToast(err.message, "error");
        } finally {
          setReopening(false);
        }
      },
    });

  // -------------------------------------------------------------------
  // API — PATCH share/unshare a single timeline entry.
  // PATCH {serverUrl}api/v1/hr/grievances/:id/timeline/:eventId/share
  //
  // NOTE: the UI already prevents opening this for the original
  // complaint entry (see `canToggleShare` in TimelineEntry), but we
  // guard here too so a stray/old event reference can never unshare the
  // employee's original submission.
  // -------------------------------------------------------------------
  const toggleShare = (event) => {
    if (event?.isOriginal) return;
    return confirmAsk({
      title: event.visibleToEmployee
        ? "Unshare Update"
        : "Share Update with Employee",
      message: event.visibleToEmployee
        ? "The employee will no longer see this update on their tracker."
        : "This update will become visible to the employee on their tracker.",
      confirmLabel: event.visibleToEmployee ? "Unshare" : "Share",
      onConfirm: async () => {
        try {
          await apiRequest(
            `api/v1/hr/grievances/${caseId}/timeline/${event.id}/share`,
            {
              method: "PATCH",
              body: { shared: !event.visibleToEmployee },
              token,
            },
          );
          await loadCase(true);
          loadActivity();
          showToast(
            !event.visibleToEmployee
              ? "Update shared with employee."
              : "Update unshared.",
          );
        } catch (err) {
          showToast(err.message, "error");
        }
      },
    });
  };

  // -------------------------------------------------------------------
  // API — POST/DELETE general case attachments.
  // -------------------------------------------------------------------
  const uploadAttachment = async (files) => {
    try {
      const form = new FormData();
      files.forEach((f) => form.append("files", f));
      await apiRequest(`api/v1/hr/grievances/${caseId}/attachments`, {
        method: "POST",
        body: form,
        token,
      });
      await loadCase(true);
      loadActivity();
      showToast("Attachment uploaded.");
    } catch (err) {
      showToast(err.message, "error");
    }
  };
  const removeNoteFile = (index) => {
    setNoteFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0 && noteFileInputRef.current)
        noteFileInputRef.current.value = "";
      return next;
    });
  };

  const deleteAttachment = (attachment) =>
    confirmAsk({
      title: "Delete Attachment",
      message: `Permanently delete "${attachment.originalName}"? This can't be undone.`,
      confirmLabel: "Delete",
      tone: "danger",
      onConfirm: async () => {
        try {
          await apiRequest(
            `api/v1/hr/grievances/${caseId}/attachments/${attachment._id}`,
            {
              method: "DELETE",
              token,
            },
          );
          await loadCase(true);
          loadActivity();
          showToast("Attachment deleted.");
        } catch (err) {
          showToast(err.message, "error");
        }
      },
    });

  // -------------------------------------------------------------------
  // API — POST a follow-up conversation message.
  //
  // Follow-up messages/attachments are purely conversational — they
  // should only ever appear in the Follow-up Conversation panel, never
  // in the Investigation Timeline or the Activity History audit trail.
  // So on send we refresh the conversation thread only; we deliberately
  // do NOT call loadActivity() or loadCase() here.
  //
  // NOTE: sending is also blocked client-side (see `isCaseLocked` /
  // ConversationPanel's `disabled` prop) whenever the case is resolved
  // or closed, so this should never actually fire in that state — but
  // we still guard here in case a stale UI state slips through.
  // -------------------------------------------------------------------
  const sendMessage = async (message, files) => {
    if (isCaseLocked) {
      showToast(
        "This case is closed. Reopen it to send a follow-up message.",
        "error",
      );
      return;
    }
    try {
      const form = new FormData();
      form.set("message", message);
      files.forEach((f) => form.append("files", f));
      await apiRequest(`api/v1/hr/grievances/${caseId}/conversation`, {
        method: "POST",
        body: form,
        token,
      });
      await loadConversation(true);
      showToast("Follow-up message sent.");
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  return (
    <div className={"min-h-full w-full " + t.textPrimary + " " + t.pageWash}>
      {/* Header */}
      <div
        className={
          "sticky top-0 z-30 border-b backdrop-blur-md " +
          t.pillBorder +
          " " +
          (isDark ? "bg-[#0c0d1f]/85" : "bg-white/85")
        }
      >
        <div className="flex items-center gap-3 px-4 sm:px-6 md:px-10 py-4">
          <button
            onClick={() => navigate(-1)}
            className={
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors " +
              t.surfaceSoft +
              " " +
              t.surfaceHover +
              " " +
              t.textMuted
            }
            aria-label="Back to Grievances"
          >
            <ChevronLeft size={18} strokeWidth={2.5} />
          </button>
          <span
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{
              backgroundColor: `${BRAND.purple}22`,
              color: BRAND.purple,
            }}
          >
            <ClipboardList size={19} strokeWidth={2} />
          </span>
          <div className="min-w-0 flex-1">
            <h1
              className={
                "truncate text-lg sm:text-xl font-bold leading-tight " +
                t.textPrimary
              }
            >
              {caseId}
            </h1>
            <p className={"flex items-center gap-1 text-[11px] " + t.textFaint}>
              <Lock size={10} /> Anonymized case file · traceable by ID only
            </p>
          </div>
          {data && (
            <div className="hidden items-center gap-2 sm:flex">
              <StatusBadge status={data.status} />
              <PriorityBadge priority={data.priority} />
            </div>
          )}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 md:px-10 py-6">
        {loading ? (
          <div
            className={
              "flex h-64 items-center justify-center gap-2 rounded-2xl text-sm " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            <RefreshCw size={16} className="animate-spin" />
            Loading case file…
          </div>
        ) : error ? (
          <div
            className={
              "flex h-64 flex-col items-center justify-center gap-2 rounded-2xl text-sm " +
              t.surfaceSoft +
              " " +
              t.textMuted
            }
          >
            <AlertTriangle size={22} className="text-[#ef4444]" />
            Couldn't load this case right now.
            <button
              onClick={() => loadCase()}
              className={
                "mt-2 rounded-full border px-4 py-1.5 text-xs font-semibold " +
                t.pillBorder +
                " " +
                t.surfaceHover
              }
            >
              Retry
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Overview strip — full width */}
            <CardShell accent="purple" padded={false}>
              <div className="p-4 sm:p-5 md:p-6">
                <SectionHeader
                  icon={Layers}
                  title="Case Overview"
                  tint={BRAND.purple}
                  t={t}
                  action={
                    <div className="flex items-center gap-2">
                      {isCaseLocked && (
                        <ReopenCaseButton
                          onReopen={reopenCase}
                          loading={reopening}
                        />
                      )}
                      <UpdateCasePanel
                        status={data.status}
                        priority={data.priority}
                        onUpdate={updateCase}
                        disabled={isCaseLocked}
                        t={t}
                      />
                    </div>
                  }
                />
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge status={data.status} />
                  <PriorityBadge priority={data.priority} />
                  <CategoryTag category={data.category} />
                </div>
                <div
                  className={
                    "mt-4 rounded-2xl px-3 sm:px-4 py-3.5 " + t.surfaceSoft
                  }
                >
                  <StatusTracker
                    status={data.status}
                    statusHistory={data.statusHistory}
                  />
                </div>
              </div>
            </CardShell>

            {/* Quick facts row — Filed / Days Pending / Handler / Reported By */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <CardShell accent="gold" className="!p-2.5">
                <div className={"rounded-2xl px-3 py-2.5 " + t.surfaceSoft}>
                  <div
                    className={
                      "flex items-center gap-1.5 text-[10px] font-semibold uppercase " +
                      t.textFaint
                    }
                  >
                    <CalendarClock size={11} /> Filed
                  </div>
                  <div className={"mt-1 text-sm font-bold " + t.textPrimary}>
                    {fmtDate(data.filedDate)}
                  </div>
                </div>
              </CardShell>
              <CardShell accent="gold" className="!p-2.5">
                <div className={"rounded-2xl px-3 py-2.5 " + t.surfaceSoft}>
                  <div
                    className={
                      "flex items-center gap-1.5 text-[10px] font-semibold uppercase " +
                      t.textFaint
                    }
                  >
                    <Hourglass size={11} /> Days Pending
                  </div>
                  <div className={"mt-1 text-sm font-bold " + t.textPrimary}>
                    {data.status === "resolved"
                      ? "Closed"
                      : `${data.daysPending}d`}
                  </div>
                </div>
              </CardShell>
              <CardShell accent="gold" className="!p-2.5">
                <AssignPanel
                  assignedTo={data.assignedTo}
                  handlers={handlers}
                  onAssign={assignHandler}
                  t={t}
                />
              </CardShell>
            </div>

            {/* Two-column body: Summary + Timeline + Add Note | Status History + Attachments + Activity */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="space-y-6 lg:col-span-2">
                <CardShell accent="teal">
                  <SectionHeader
                    icon={FileText}
                    title="Grievance Summary"
                    tint={BRAND.teal}
                    t={t}
                  />
                  <p
                    className={
                      "max-h-40 overflow-y-auto rounded-2xl px-4 py-3.5 text-sm leading-relaxed " +
                      t.surfaceSoft +
                      " " +
                      t.textMuted
                    }
                  >
                    {data.summary}
                  </p>
                </CardShell>

                <CardShell accent="pink">
                  <SectionHeader
                    icon={ListChecks}
                    title="Investigation Timeline"
                    tint={BRAND.pink}
                    count={data.timeline.length}
                    t={t}
                  />
                  <div className="max-h-[440px] overflow-y-auto pr-1">
                    {data.timeline.map((event, i) => (
                      <TimelineEntry
                        key={event.id ?? i}
                        event={event}
                        isLast={i === data.timeline.length - 1}
                        isOriginal={i === 0}
                        isDark={isDark}
                        t={t}
                        onToggleShare={toggleShare}
                        onPreview={setPreviewAttachment}
                      />
                    ))}
                  </div>

                  {/* Add Investigation Note — only available while the case
                      is active. Once resolved/closed this is hidden entirely
                      (same as before); reopening the case restores it since
                      `data.status` will no longer be "resolved"/"closed". */}
                  {data.status !== "resolved" && data.status !== "closed" && (
                    <div
                      className="mt-2 border-t pt-5"
                      style={{ borderColor: t.gridStroke }}
                    >
                      <h5
                        className={
                          "mb-2 text-xs font-bold uppercase tracking-wide " +
                          t.textFaint
                        }
                      >
                        Add Investigation Note
                      </h5>
                      <div className={"rounded-2xl p-2 " + t.surfaceSoft}>
                        <div className="flex items-end gap-2">
                          <textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Log an update, interview finding, or next step…"
                            rows={2}
                            className={
                              "flex-1 resize-none bg-transparent px-2 py-1.5 text-sm outline-none placeholder:" +
                              t.textFaint +
                              " " +
                              t.textPrimary
                            }
                          />
                          <button
                            onClick={addNote}
                            disabled={submittingNote || !note.trim()}
                            className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                            style={{ background: BRAND.gradient }}
                          >
                            <Send size={13} />
                            {submittingNote ? "Adding…" : "Add Note"}
                          </button>
                        </div>
                        {noteFiles.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5 px-2">
                            {noteFiles.map((f, i) => (
                              <span
                                key={i}
                                className={
                                  "flex items-center gap-1 rounded-full pl-2 pr-1 py-0.5 text-[10px] " +
                                  t.surface +
                                  " " +
                                  t.textMuted
                                }
                              >
                                <Paperclip size={9} /> {f.name}
                                <button
                                  type="button"
                                  onClick={() => removeNoteFile(i)}
                                  className="ml-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full hover:bg-black/10"
                                  aria-label={`Remove ${f.name}`}
                                >
                                  <X size={9} strokeWidth={3} />
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <button
                          onClick={() => noteFileInputRef.current?.click()}
                          className={
                            "mt-1.5 ml-1 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors " +
                            t.surfaceHover +
                            " " +
                            t.textMuted
                          }
                        >
                          <Paperclip size={11} /> Attach files to this note
                        </button>
                        <input
                          ref={noteFileInputRef}
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,image/*"
                          className="hidden"
                          onChange={(e) =>
                            setNoteFiles(Array.from(e.target.files || []))
                          }
                        />
                      </div>

                      <div className="mt-2.5 flex flex-wrap items-center justify-between gap-3">
                        <label
                          className={
                            "flex w-fit cursor-pointer items-center gap-2 text-[11px] font-medium " +
                            t.textMuted
                          }
                        >
                          <input
                            type="checkbox"
                            checked={shareWithEmployee}
                            onChange={(e) =>
                              setShareWithEmployee(e.target.checked)
                            }
                            className="h-3.5 w-3.5 rounded accent-[#8b7cf6]"
                          />
                          Share this update with the employee on their tracker
                        </label>

                        <button
                          onClick={markResolved}
                          disabled={resolving}
                          className="flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold text-white transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                          style={{ backgroundColor: BRAND.teal }}
                        >
                          <CheckCircle2 size={14} />
                          {resolving ? "Resolving…" : "Mark Resolved"}
                        </button>
                      </div>
                    </div>
                  )}
                </CardShell>
              </div>

              {/* Sidebar */}
              <div className="space-y-6 lg:col-span-1">
                <CardShell accent="gold">
                  <SectionHeader
                    icon={History}
                    title="Status History"
                    tint={BRAND.gold}
                    count={data.statusHistory.length}
                    t={t}
                  />
                  <div className="max-h-56 space-y-2 overflow-y-auto pr-1">
                    {data.statusHistory.map((h, i) => (
                      <div
                        key={i}
                        className={
                          "flex items-center justify-between gap-2 rounded-xl px-2.5 py-2 text-[11px] font-medium " +
                          t.surfaceSoft
                        }
                      >
                        <StatusBadge status={h.status} size="sm" />
                        <span className={t.textFaint}>
                          {fmtDateTime(h.timestamp)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardShell>

                <CardShell accent="purple">
                  <ActivityHistoryPanel
                    activity={activity}
                    loading={activityLoading}
                    t={t}
                  />
                </CardShell>
              </div>
            </div>

            {/* Follow-up conversation — full width */}
            <CardShell accent="purple">
              <ConversationPanel
                conversation={conversation}
                loading={conversationLoading}
                disabled={isCaseLocked}
                isDark={isDark}
                t={t}
                onSend={sendMessage}
                onPreview={setPreviewAttachment}
              />
            </CardShell>
          </div>
        )}
      </div>

      <Toast toast={toast} onDismiss={() => setToast(null)} />
      {confirmDialog}
      <AttachmentPreviewModal
        attachment={previewAttachment}
        onClose={() => setPreviewAttachment(null)}
        t={t}
        isDark={isDark}
      />
    </div>
  );
}
